import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Logo3D from "@/components/hud/Logo3D";
import { CheckCircle2, XCircle, Loader2, Package, MapPin, Gem, Unlock, ArrowRight } from "lucide-react";

export default function OrderReturn() {
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const navigate = useNavigate();
  const polled = useRef(false);

  useEffect(() => {
    if (polled.current) return;
    polled.current = true;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 8) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/shop/order/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setOrder(res.data.order);
          setStatus("success");
          clear();
          return;
        }
        if (res.data.status === "expired") { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reward = order?.reward;

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-5 relative" data-testid="order-return-page">
      <div className="absolute inset-0 hud-grid opacity-20" />
      <div className="relative glass-strong clip-chamfer brackets p-10 max-w-md w-full text-center">
        {status === "checking" && (
          <>
            <Logo3D size={110} />
            <Loader2 className="w-6 h-6 text-cyan animate-spin mx-auto mt-6" />
            <div className="font-mono text-xs tracking-[0.3em] text-cyan/70 mt-4">CONFIRMING ORDER...</div>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-cyan mx-auto" />
            <h1 className="font-head text-3xl uppercase font-bold mt-5 text-glow-cyan">Order Confirmed</h1>
            <p className="text-slate-400 mt-2 font-mono text-xs">{order?.order_id}</p>

            {/* Rewards earned on this order */}
            {reward && (
              <div className="glass p-4 mt-5 text-left space-y-3 border border-cyan/25" data-testid="order-reward">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-mono text-[11px] text-cyan">
                    <Gem className="w-4 h-4" /> +{reward.points_earned} AETHER POINTS
                  </div>
                  <span className="font-mono text-[10px] text-slate-400">BALANCE {reward.balance}</span>
                </div>
                {(reward.multiplier > 1 || reward.bonus > 0) && (
                  <div className="flex flex-wrap gap-2" data-testid="reward-boosters">
                    {reward.multiplier > 1 && (
                      <span className="font-mono text-[9px] tracking-widest px-2 py-0.5 border border-amber/50 text-amber">{reward.multiplier}× WEEKEND</span>
                    )}
                    {reward.bonus > 0 && (
                      <span className="font-mono text-[9px] tracking-widest px-2 py-0.5 border border-cyan/50 text-cyan">+{reward.bonus} FIRST-ORDER BONUS</span>
                    )}
                  </div>
                )}
                {reward.points_spent > 0 && (
                  <div className="font-mono text-[10px] text-slate-500">Redeemed −{reward.points_spent} pts on this order</div>
                )}
                {reward.unlocked?.length > 0 && (
                  <div className="pt-2 border-t border-cyan/10">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-amber mb-2">
                      <Unlock className="w-3.5 h-3.5" /> SIMULATION UNLOCKED
                    </div>
                    {reward.unlocked.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => navigate(`/simulation/world/${s.id}`)}
                        data-testid={`unlocked-${s.id}`}
                        className="w-full group relative h-20 overflow-hidden border border-white/10 hover:border-cyan transition-all mb-2"
                      >
                        <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
                        <div className="absolute inset-0 bg-gradient-to-r from-app via-app/60 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-between px-3">
                          <div className="text-left">
                            <div className="font-mono text-[8px] tracking-widest" style={{ color: s.accent }}>{s.biome?.toUpperCase()}</div>
                            <div className="font-head text-sm uppercase text-slate-100">{s.name}</div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-cyan" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="glass p-4 mt-3 text-left space-y-2">
              <div className="flex items-center gap-2 font-mono text-[11px] text-amber">
                <Package className="w-4 h-4" /> FULFILLMENT: {order?.fulfillment?.status?.replace(/_/g, " ").toUpperCase()}
              </div>
              <div className="flex items-center gap-2 font-mono text-[11px] text-slate-400">
                <MapPin className="w-4 h-4 text-cyan" /> {order?.fulfillment?.partner} · {order?.fulfillment?.city || "Nicosia"}, {order?.fulfillment?.country || "Cyprus"}
              </div>
              {order?.shipping_address && (
                <div className="font-mono text-[10px] text-slate-500 leading-relaxed pt-1 border-t border-cyan/10">
                  SHIP TO: {order.shipping_address.full_name}, {order.shipping_address.line1}
                  {order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}, {order.shipping_address.city} {order.shipping_address.postal_code}, {order.shipping_address.country}
                </div>
              )}
              {order?.costs && (
                <div className="pt-1 border-t border-cyan/10 font-mono text-[10px] text-slate-400 space-y-0.5">
                  <div className="flex justify-between"><span>SUBTOTAL</span><span>${order.costs.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>SHIPPING</span><span>${order.costs.shipping?.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>TAX</span><span>${order.costs.tax?.toFixed(2)}</span></div>
                  {order.costs.discount > 0 && (
                    <div className="flex justify-between text-cyan"><span>POINTS DISCOUNT</span><span>−${order.costs.discount?.toFixed(2)}</span></div>
                  )}
                </div>
              )}
              <div className="font-mono text-[11px] text-slate-400 pt-1 border-t border-cyan/10">TOTAL: <span className="text-cyan">${order?.amount?.toFixed(2)}</span></div>
            </div>
            <button onClick={() => navigate("/shop")} data-testid="order-continue-btn" className="btn-hud mt-6 w-full border border-cyan text-cyan py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app">
              Continue Shopping
            </button>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="w-16 h-16 text-amber mx-auto" />
            <h1 className="font-head text-3xl uppercase font-bold mt-5 text-amber">
              {status === "timeout" ? "Confirmation Delayed" : "Order Not Completed"}
            </h1>
            <p className="text-slate-400 mt-3">
              {status === "timeout" ? "Payment is still processing. Check your orders shortly." : "The transaction was not completed."}
            </p>
            <button onClick={() => navigate("/shop/cart")} data-testid="order-retry-btn" className="btn-hud mt-6 w-full border border-amber/50 text-amber py-3 text-sm hover:bg-amber hover:text-app">
              Back to Cart
            </button>
          </>
        )}
      </div>
    </div>
  );
}
